import {observable, React, _} from '@api'
import {CatalogSearcherStore, ICatalogSearchResult} from '@modules'
import {ICatalogFieldConfig, LibraryItemConfig, onSiteError, CatalogStore} from '@stores'
import {CatalogInstanceChildStore} from '@stores/catalog/CatalogInstanceChildStore'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {MyImage, Placeholder} from 'centric-ui-components'
import {action, autorun, computed, reaction, runInAction, toJS, when} from 'mobx'
import { removeUndefinedsNested } from '@utils'
import {IDisposer} from 'mobx-utils'
import {IReactionDisposer} from 'mobx/lib/internal'

export interface GotoCatalogItemPayload extends ICatalogSearchResult<{ workspace: string, _id: string, item?: any }> {
}

/**
 * Responsible for loading remote results from ES for a given search string
 */
export class CatalogRemoteSearcherStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)
    const {searcher} = this

    // Can be preset from query params/refresh
    synchronizeStores(() => {
      let reactionFn : IReactionDisposer
      this._toDispose.push(
        autorun(() => {
          if (searcher.popoverOpen) {
            if (!reactionFn) {
              this.searching = true
              this.runQuery()

              reactionFn = reaction(() => ({searchText: this.searchText, filter: catalog.es.catalogItemFilter}), () => {
                const {searchText} = this, {es: {catalogItemFilter}} = catalog
                this.searching = true
                this._debounceRunQuery()
              })
            }
          } else {
            reactionFn && reactionFn()
            reactionFn = null
          }
        },
        {name: `Run search text query for CatalogSearcher (debounced)`}))
    })
  }

  _toDispose: IDisposer[] = []

  @action dispose = () => this._toDispose.forEach(f => f())

  get searcher() {
    return this.catalog.searcher
  }

  @observable searching = false

  get searchText() { return this.searcher.searchQueryText }

  @action private _debounceRunQuery = _.debounce(
    async () => {
      try {
        await this.runQuery()
      } catch (err) {
        onSiteError(err)
      }
    }, 250, {leading: false, trailing: true})

  @observable searchTextResultsCount: number = null
  @observable loadingSearchTextResultsCount = false

  @action runQuery = async () => {
    try {
      if (!this.searchText) return

      // console.log(`Run remote search query (expensive) - ${this.searchText}`)

      // Start querying for filter values, library values, catalog items, catalog items, etc to show live results
      const {
        es, catalog, catalog: {filter: {filterFields}},
        searchLibraryFor, searchFilterValuesFor, searcher: {searchableLibraries}
      } = this

      const promises = await Promise.all(
        [
          ...searchableLibraries.filter(l => l.index != es.indexes.tag).map(l => searchLibraryFor(l)),
          ...filterFields.map(f => searchFilterValuesFor(f)),
          //searchCatalogItems() // Only local to workspace, not useful
        ])

      runInAction(`Update remote search results after all search promises have resolved`, () => {
        const {resultsDict} = this
        this.remoteSearchResults = _.chain([
          //loading && {title: `Searching 8 places for '${searchText}'...`, groupLabel: 'Remote Search'},
          ..._.chain(resultsDict).keys().orderBy(k => k == catalog.index ? 0 : 1).map(k => resultsDict[k].results).flatten().value()
        ]).compact().value()
      })

      //console.log(_.chain(promises).flatten().compact().value())
    } finally {
      this.searching = false
    }
  }

  @action searchLibraryFor = async (library: LibraryItemConfig): Promise<Array<ICatalogSearchResult>> => {
    const {strings, es, es: {search, unwrapHitsResult}, catalog, resultsDict} = this,
      {index, where, name} = library,
      dataMap = catalog.dataMapForLibrary(library),
      lowercaseSearch = _.toLower(this.searchText),
      groupLabel = `${_.startCase(library.label ?? library.name)}`,
      lowercaseGroupLabel = _.toLower(groupLabel),
      associatedWorkspace = catalog.workspaceByIndex[index]

    // Only include search terms that don't match our library name so the user can search for 'red vendor product'
    const searchText = lowercaseSearch.split(' ').filter(s => lowercaseGroupLabel.indexOf(s) === -1).join(' ')

    if (!index || !lowercaseSearch) return null

    resultsDict[name] = Object.assign({}, resultsDict[name], {loading: true, searchText})
    const state = resultsDict[name],
      qFilter = (where ? where.filter(w => w?.field).map(({ field, value }) => ({ [_.isString(value) ? 'match' : 'term']: { [field]: value } })) : []).concat([/*es.filterForNonDeletedItems,*/ ...catalog.es.seasonSelectorRestrictedTermsFor(associatedWorkspace)]).filter(_.identity)
    try {
      const searchResultsRaw = unwrapHitsResult(
        await search(removeUndefinedsNested({
          index: library.index,
          size: 50,
          meta: `Search results for library ${library.index}`,
          body: {
            query: {
              bool: {
                ...(_.size(qFilter) && { filter: qFilter }),
                must: {
                  query_string: {
                    fuzziness: 'AUTO',
                    query: `*${es.tokenizeQueryString(searchText)}*`,
                    fields: _.uniq([
                      ..._.values(dataMap).filter(_.identity).map(f => `${f}.text`),
                      ..._.values(dataMap).filter(_.identity).filter(f => catalog.isTextField(f, catalog.workspaceByIndex[library.index])).map(f => `${f}.text`),
                      ...(associatedWorkspace?.catalog?._searchFields ?? [])
                    ]),
                    default_operator: 'AND'
                  }
                }
              }
            }
          }
        }), {log: {query: false, result: false}})
      )

      if (state.searchText !== searchText) {
        // Our result - otherwise user kept typing and we should discard this result
        console.info(`Ignoring result for prior search text '${searchText}' vs current '${state.searchText}'`)
      } else {
        state.results = searchResultsRaw.items.map((item: any) => {
          const image = _.first(_.get(item, dataMap.images))
          return ({
            groupLabel,
            type: 'card',
            action: 'gotoLibraryItem',
            group: library.name,
            value: {_id: catalog.idFor(item), workspace: associatedWorkspace?._id, value: item, library},
            //value: {library, value: item},
            actionLabel: _.get(item, dataMap.subtitle1),
            title: _.get(item, dataMap.title) ?? strings.emptyLabel,
            subtitle: dataMap.subtitle2 ? (_.get(item, dataMap.subtitle2) ?? strings.emptyLabel) : undefined,
            icon: image ? <MyImage imageUrl={catalog.imageUrlFor(image)}/> : <Placeholder type={'small'}/>
          })
        })
      }
    } catch (err) {
      console.error(err)
    }

    state.loading = false
    return state.results
  }

  @action searchFilterValuesFor = async (filterField: ICatalogFieldConfig): Promise<Array<ICatalogSearchResult>> => {
    return []
  }

  @observable resultsDict: { [name: string]: { loading?: boolean, results?: Array<ICatalogSearchResult>, searchText?: string } } = {}

  @observable _loading = false
  @computed get loading() {
    return this._loading || _.values(this.resultsDict).some(d => d.loading)
  }

  @action searchCatalogItems = async (): Promise<Array<ICatalogSearchResult>> => {
    const {strings, es, catalog} = this
    if (!catalog.es.catalogItemFilter || !catalog.workspaceConfig) return []

    const {catalog: {index, dataMap, searchFieldsEs, es: {catalogItemFilter: {filter: {query: {bool: {filter}}}}}}, es: {search, unwrapHitsResult}, resultsDict} = this,
      lowercaseSearch = _.toLower(this.searchText),
      lowercaseCatalogItemName = _.toLower(catalog.catalogItemName)

    // Only include search terms that don't match our library name so the user can search for 'red vendor product'
    const searchText = lowercaseSearch.split(' ').filter(s => lowercaseCatalogItemName.indexOf(s) === -1).join(' ')

    resultsDict[index] = Object.assign({}, resultsDict[index], {loading: true, searchText})
    const state = resultsDict[index]

    this.loadingSearchTextResultsCount = true
    const searchResultsRaw = unwrapHitsResult(
      await search(removeUndefinedsNested({
        index,
        size: 50,
        meta: `Search results for workspace ${catalog.workspaceConfig?.title}`,
        body: {
          query: {
            bool: {
              filter: [/*es.filterForNonDeletedItems,*/ ...filter, ...catalog.es.seasonSelectorRestrictedTermsFor(catalog.workspaceConfig)].filter(_.identity),
              must: {
                query_string: {
                  fuzziness: 'AUTO',
                  query: `*${es.tokenizeQueryString(searchText)}*`,
                  fields: searchFieldsEs,
                  default_operator: 'AND'
                }
              }
            }
          }
        }
      }), {log: {query: false, result: false}})
    )

    if (state.searchText !== searchText) {
      // Our result - otherwise user kept typing and we should discard this result
      console.info(`Ignoring result for prior search text '${searchText}' vs current '${state.searchText}'`)
    } else {
      runInAction(() => {
        const filteredOfflineItems = catalog._filterOfflineItems(null, catalog.offlineItemsOfCurrentWorkspace, searchText, false)
        this.searchTextResultsCount = searchResultsRaw?.total?.value + filteredOfflineItems.length
        this.loadingSearchTextResultsCount = false

        console.log(toJS(searchResultsRaw))

      state.results = [...filteredOfflineItems, ...searchResultsRaw.items].map((item: any) => {
        const image = _.first(catalog.imagesForCatalogItem(item))
        return ({
          groupLabel: `${catalog.catalogItemName}`,
          type: 'card',
          action: 'gotoCatalogItem',
          group: 'catalogItem',
          value: {_id: catalog.idFor(item), workspace: ''}, //searchResultsRaw},
          actionLabel: _.get(item, dataMap.subtitle1),
          title: _.get(item, dataMap.title) ?? strings.emptyLabel,
          icon: image ? <MyImage imageUrl={catalog.imageUrlFor(image)}/> : <Placeholder type={'small'}/>
        }) as  GotoCatalogItemPayload
      })
      })
    }

    state.loading = false
    return state.results
  }

  @observable.ref remoteSearchResults: Array<ICatalogSearchResult> = []
}




// WEBPACK FOOTER //
// ./src/modules/catalogSearcher/CatalogRemoteSearcherStore.tsx